import React, { useState } from 'react';
import { Container, Card, Form, Table, Spinner, Badge, Button } from 'react-bootstrap';
import { RefreshCw, Package, ExternalLink, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IdBadge, IdBadgeType } from '../../../components/ui/badge/IdBadge';
import { getCMWorkflowStatusColor } from '../../../components/ui/badge/StatusBadge';
import { useCMWorkflowList } from '../../../apps/hooks/sourcing/useCMWorkflowList';
import type { CMWorkflowItem } from '../../../models/CatalogManagement';
import { WORKFLOW_STEP_LABELS, WORKFLOW_ORDER } from '../../../models/CatalogManagement';
import { ROUTES } from '../../../routes/routes';
import { StatisticsCard } from './components/StatisticsCard';
import { convertToLocalTime } from '../../../utils/dateUtils';

const CMWorkflowPage: React.FC = () => {
  const {
    workflows,
    loading,
    statistics,
    loadingStats,
    pagination,
    filters,
    loadMore,
    refresh,
    updateFilter,
    setItemsPerPage,
    deleteWorkflow,
  } = useCMWorkflowList();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getLastHistoryEntry = (workflow: CMWorkflowItem) => {
    return workflow.lastHistory || null;
  };

  const getFirstSku = (workflow: CMWorkflowItem): string | null => {
    if (workflow.skuCpiiMappings && workflow.skuCpiiMappings.length > 0) {
      return workflow.skuCpiiMappings[0]?.sku ?? null;
    }
    if (workflow.skus && workflow.skus.length > 0) {
      return workflow.skus[0] ?? null;
    }
    return null;
  };

  const getStepLabel = (status: string, type: 'before' | 'after' = 'before'): string => {
    const stepInfo = WORKFLOW_STEP_LABELS.find((step) => step.id === status);
    if (!stepInfo) {
      return status;
    }
    return type === 'before' ? stepInfo.labelBefore : stepInfo.labelAfter;
  };

  const sortedWorkflows = React.useMemo(() => {
    return [...workflows].sort((a, b) => {
      const dateA = new Date(a.lastHistory?.updatedAt || a.modifiedAt).getTime();
      const dateB = new Date(b.lastHistory?.updatedAt || b.modifiedAt).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [workflows, sortOrder]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const handleDeleteClick = async (workflow: CMWorkflowItem) => {
    if (!workflow.gpi || !workflow.cppi) {
      return;
    }

    if (
      !confirm(
        `Are you sure you want to delete this CM workflow?\n\nGPI: ${workflow.gpi}\nCPPI: ${workflow.cppi}\nStatus: ${getStepLabel(workflow.cmStatus, 'after')}\n\nThis action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await deleteWorkflow(workflow.gpi, workflow.cppi);
    } catch (error) {
      alert(`Failed to delete workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <Container fluid className="py-3">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="h2 mb-1">CM Workflow</h4>
          <p className="text-muted mb-0">View catalog management workflow status for all products</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="outline-primary" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </Button>
          <Form.Select
            size="sm"
            value={pagination.itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
        </div>
      </div>

      {/* Statistics Section */}
      <StatisticsCard statistics={statistics} loading={loadingStats} />

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Label className="small fw-semibold">CM Current Status</Form.Label>
              <Form.Select
                size="sm"
                value={filters.cmStatus}
                onChange={(e) => updateFilter('cmStatus', e.target.value)}
              >
                <option value="">All Statuses</option>
                {WORKFLOW_ORDER.map((status, index) => (
                  <option key={status} value={status}>
                    {index + 1}. {getStepLabel(status, 'before')}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label className="small fw-semibold">GPI Search</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Enter GPI..."
                  value={filters.gpi}
                  onChange={(e) => updateFilter('gpi', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      refresh();
                    }
                  }}
                />
                <Button size="sm" variant="primary" onClick={refresh} disabled={loading}>
                  Search
                </Button>
              </div>
            </div>
            <div className="col-md-3">
              <Form.Label className="small fw-semibold">Month</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="e.g., 2025-01"
                value={filters.month}
                onChange={(e) => updateFilter('month', e.target.value)}
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card>
        <Card.Body className="p-0">
          {loading && workflows.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Loading workflows...</p>
            </div>
          ) : workflows.length === 0 ? (
            <div className="text-center py-5">
              <Package size={48} className="text-muted mb-3" />
              <h5>No workflows found</h5>
              <p className="text-muted">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table className="mb-0" bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '80px', fontSize: '0.8rem', fontWeight: '600' }}>Month</th>
                      <th
                        style={{
                          width: '150px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={toggleSortOrder}
                      >
                        <div className="d-flex align-items-center gap-1">
                          Updated Time
                          {sortOrder === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                        </div>
                      </th>
                      <th style={{ width: '140px', fontSize: '0.8rem', fontWeight: '600' }}>CM Current Status</th>
                      <th style={{ width: '180px', fontSize: '0.8rem', fontWeight: '600' }}>Operator</th>
                      <th style={{ width: '100px', fontSize: '0.8rem', fontWeight: '600' }}>Reason</th>
                      <th style={{ width: '200px', fontSize: '0.8rem', fontWeight: '600' }}>Message</th>
                      <th style={{ width: '100px', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600' }}>
                        SS Editor
                      </th>
                      <th style={{ width: '120px', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600' }}>
                        GP-CP Matched
                      </th>
                      <th style={{ width: '120px', fontSize: '0.8rem', fontWeight: '600' }}>GPI</th>
                      <th style={{ width: '120px', fontSize: '0.8rem', fontWeight: '600' }}>CPPI</th>
                      <th style={{ fontSize: '0.8rem', fontWeight: '600' }}>SKUs</th>
                      <th style={{ width: '80px', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedWorkflows.map((workflow, index) => {
                      const lastHistory = getLastHistoryEntry(workflow);
                      const firstSku = getFirstSku(workflow);
                      const currentStatus = workflow.lastHistory?.toStatus || workflow.cmStatus;
                      const currentDate = workflow.lastHistory?.updatedAt || workflow.modifiedAt;
                      return (
                        <tr key={`${workflow.gpi}-${workflow.cppi}-${index}`}>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            <span style={{ fontSize: '0.75rem' }}>{workflow.month || 'N/A'}</span>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            <span style={{ fontSize: '0.7rem', color: '#666' }}>
                              {currentDate ? convertToLocalTime(currentDate) : 'N/A'}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            <Badge bg={getCMWorkflowStatusColor(currentStatus)} style={{ fontSize: '0.65rem' }}>
                              {getStepLabel(currentStatus, 'after')}
                            </Badge>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#666' }}>{lastHistory?.operator || 'N/A'}</span>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            {lastHistory?.reason ? (
                              <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                                {lastHistory.reason}
                              </Badge>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>-</span>
                            )}
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            <span
                              style={{
                                fontSize: '0.7rem',
                                color: '#666',
                                display: 'block',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={lastHistory?.message || ''}
                            >
                              {lastHistory?.message || '-'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '6px' }}>
                            {firstSku ? (
                              <Link
                                to={ROUTES.catalog.ssEditorWithSku(firstSku)}
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                                title="Edit in SS Editor"
                              >
                                <ExternalLink size={12} /> SS Editor
                              </Link>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>-</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '6px' }}>
                            {workflow.gpi ? (
                              <Link
                                to={ROUTES.sourcing.matchedGpi(workflow.gpi)}
                                className="btn btn-outline-primary btn-sm"
                                style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                                title="View Matched GPI"
                              >
                                <ExternalLink size={12} /> Matched
                              </Link>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>-</span>
                            )}
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            {workflow.gpi ? (
                              <IdBadge type={IdBadgeType.GPI} value={workflow.gpi} size="sm" />
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>N/A</span>
                            )}
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            {workflow.cppi ? (
                              <IdBadge type={IdBadgeType.CPPI} value={workflow.cppi} size="sm" />
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>N/A</span>
                            )}
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '6px' }}>
                            <div className="d-flex flex-wrap gap-1">
                              {workflow.skuCpiiMappings && workflow.skuCpiiMappings.length > 0 ? (
                                workflow.skuCpiiMappings.map((mapping, idx) => (
                                  <IdBadge key={idx} type={IdBadgeType.SKU} value={mapping.sku} size="sm" />
                                ))
                              ) : workflow.skus && workflow.skus.length > 0 ? (
                                workflow.skus.map((sku, idx) => (
                                  <IdBadge key={idx} type={IdBadgeType.SKU} value={sku} size="sm" />
                                ))
                              ) : (
                                <span style={{ fontSize: '0.75rem', color: '#999' }}>N/A</span>
                              )}
                            </div>
                          </td>
                          <td style={{ width: '80px', textAlign: 'center', verticalAlign: 'middle', padding: '6px' }}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(workflow)}
                              disabled={!workflow.gpi || !workflow.cppi}
                              title="Delete workflow"
                              style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Load More Button */}
              {pagination.hasNextPage && (
                <div className="text-center p-3 border-top">
                  <Button variant="outline-primary" size="sm" onClick={loadMore} disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Summary */}
      <div className="mt-3 text-muted small">
        <p className="mb-0">
          Showing {workflows.length} workflow{workflows.length !== 1 ? 's' : ''}
          {pagination.hasNextPage && ' (more available)'}
        </p>
      </div>
    </Container>
  );
};

export default CMWorkflowPage;
